/**
 * Phase 55.1 — USM Messenger V1.1 context.
 *
 * Additive over V1: adds conversations map (general / dm:*), roster,
 * presence (online + last seen), read pointers (mine + others'),
 * typing signals, reactions state, and mention detection.
 *
 * The V1 API surface (`chat`, `announcements`, `unread`) remains
 * available for backward compat with any existing consumer, now
 * derived from the conversations map.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import usmApi, { USM_TOKEN_KEY } from "@/lib/usmApi";
import { toast } from "sonner";

const Ctx = createContext(null);

function currentOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return process.env.REACT_APP_BACKEND_URL || "";
}

function ding() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = window.__usmMsgAudio || (window.__usmMsgAudio = new AC());
    if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* noop */ } }
    const now = ctx.currentTime;
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.setValueAtTime(660, now);
    o.frequency.exponentialRampToValueAtTime(990, now + 0.09);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    o.start(now); o.stop(now + 0.2);
  } catch { /* noop */ }
}

// Best-effort desktop notification. Requests permission lazily on first
// unread event. Never blocks the send path.
function desktopNotify(title, body, onClick) {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") {
      const n = new Notification(title, { body, silent: true });
      if (onClick) n.onclick = () => { try { window.focus(); onClick(); } catch { /* noop */ } };
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().catch(() => { /* noop */ });
    }
  } catch { /* noop */ }
}

export function MessengerProvider({ children, currentUserId }) {
  const [open, setOpen] = useState(false);
  const [activeConversation, setActiveConversation] = useState("general");
  const [tab, setTab] = useState("general");   // general | dm | announcements
  const [conversations, setConversations] = useState({
    general: { messages: [], loaded: false, unread: 0 },
    announcements: { messages: [], loaded: false, unread: 0 },
  });
  // Phase 55.2 — Pinned messages per conversation. Shape: { [convId]: pin | null }
  const [pins, setPins] = useState({});
  // Phase 55.2 — Draft reply target per conversation. Shape: { [convId]: msg | null }
  const [replyTargets, setReplyTargets] = useState({});
  const [dmList, setDmList] = useState([]);
  const [dmListLoaded, setDmListLoaded] = useState(false);
  const [roster, setRoster] = useState([]);
  const [presence, setPresence] = useState({ online: new Set(), lastSeen: {} });
  const [readPointers, setReadPointers] = useState({ mine: {}, othersGeneral: {} });
  const [typingByConv, setTypingByConv] = useState({}); // convId → {uid → {name, ts}}
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const openRef = useRef(open);
  const activeRef = useRef(activeConversation);
  const userIdRef = useRef(currentUserId);
  const dmListRef = useRef(dmList);
  const conversationsRef = useRef(conversations);
  const ensureConvHistoryRef = useRef(null); // 55.2.2 — indirection so `connect` can call it despite the ordering
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { activeRef.current = activeConversation; }, [activeConversation]);
  useEffect(() => { userIdRef.current = currentUserId; }, [currentUserId]);
  useEffect(() => { dmListRef.current = dmList; }, [dmList]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  // ── Conversation cache helpers ──────────────────────────────
  const upsertMessage = useCallback((convId, msg) => {
    setConversations((prev) => {
      const c = prev[convId] || { messages: [], loaded: false, unread: 0 };
      const exists = c.messages.some((m) => m.id === msg.id);
      const messages = exists
        ? c.messages.map((m) => m.id === msg.id ? { ...m, ...msg } : m)
        : [...c.messages, msg];
      return { ...prev, [convId]: { ...c, messages } };
    });
  }, []);
  const patchMessage = useCallback((convId, id, patch) => {
    setConversations((prev) => {
      const c = prev[convId]; if (!c) return prev;
      return { ...prev, [convId]: { ...c, messages: c.messages.map((m) => m.id === id ? { ...m, ...patch } : m) } };
    });
  }, []);

  // Phase 55.2 — When a message is soft-deleted, mark every reply
  // snapshot that references it as deleted so the quoted preview
  // flips to "Bu mesaj silindi." across the entire conversation cache.
  const cascadeReplySnapshotDeleted = useCallback((deletedId) => {
    setConversations((prev) => {
      let mutated = false; const next = { ...prev };
      for (const [conv, cache] of Object.entries(prev)) {
        if (!cache?.messages) continue;
        let any = false;
        const messages = cache.messages.map((m) => {
          if (m.reply_to_id === deletedId && m.reply_to && !m.reply_to.is_deleted) {
            any = true;
            return { ...m, reply_to: { ...m.reply_to, is_deleted: true, body: "", has_attachment: false } };
          }
          return m;
        });
        if (any) { next[conv] = { ...cache, messages }; mutated = true; }
      }
      return mutated ? next : prev;
    });
  }, []);

  // ── WebSocket ───────────────────────────────────────────────
  const connect = useCallback(() => {
    const jwt = localStorage.getItem(USM_TOKEN_KEY) || "";
    if (!jwt) return;
    const url = `${currentOrigin().replace(/^http/, "ws")}/api/usm/messenger/ws?token=${encodeURIComponent(jwt)}`;
    let ws;
    try { ws = new WebSocket(url); } catch { return; }
    wsRef.current = ws;
    // 55.2.1 — 25 s heartbeat. Reverse proxies (nginx / Emergent
    // ingress / cloudflare) idle-close a websocket after ~60 s of
    // silence, which was orphaning presence entries and making every
    // operator appear offline. A tiny ping keeps the pipe alive and,
    // if the connection has silently died, forces onclose to fire so
    // reconnect kicks in immediately.
    let hb = null;
    const startHeartbeat = () => {
      if (hb) clearInterval(hb);
      hb = setInterval(() => {
        try {
          if (ws.readyState === 1) ws.send("ping");
          else if (ws.readyState >= 2) { clearInterval(hb); }
        } catch { /* noop */ }
      }, 25000);
    };
    const stopHeartbeat = () => { if (hb) { clearInterval(hb); hb = null; } };
    ws.onopen = () => {
      // 55.2.2 — on every (re)connect, catch up by pulling fresh
      // history for the visible conversations. If we were offline
      // when a peer sent something, this is what makes it appear
      // instantly instead of after F5. Also refresh the DM roster
      // in case a brand-new DM landed while the socket was down.
      const wasReconnect = retryRef.current > 0;
      retryRef.current = 0;
      startHeartbeat();
      if (wasReconnect) {
        try {
          const active = activeRef.current;
          const ech = ensureConvHistoryRef.current;
          if (ech) {
            ech("general", { force: true });
            if (active && active !== "general" && active !== "announcements") {
              ech(active, { force: true });
            }
          }
          usmApi.get("/messenger/dm/conversations").then((r) => {
            setDmList(r.data || []); setDmListLoaded(true);
          }).catch(() => { /* noop */ });
        } catch { /* noop */ }
      }
    };
    ws.onmessage = (ev) => {
      let msg = null;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg) return;
      const t = msg.type;
      const uid = userIdRef.current;
      if (t === "hello" || t === "presence") {
        // 55.2.1 — the initial `hello` frame now ships the full
        // online set, so we always trust `online_ids` when it's
        // present. When it's absent (e.g. legacy backend), keep the
        // previous set intact instead of wiping it to empty.
        if (Array.isArray(msg.online_ids)) {
          const online = new Set(msg.online_ids);
          // Ensure we always see OURSELVES online while our socket
          // is up — belt & braces in case a race caused the initial
          // roster snapshot to miss our just-added entry.
          if (uid) online.add(uid);
          setPresence((p) => ({ online, lastSeen: p.lastSeen }));
        }
      } else if (t === "chat.created" || t === "dm.created") {
        const conv = msg.conversation_id || (t === "chat.created" ? "general" : null);
        if (!conv) return;
        upsertMessage(conv, msg.message);
        // Ensure DM conversation entry exists in the sidebar list.
        // 55.2.2 — immediately refresh the DM roster so a first-ever
        // DM from a new peer appears in the sidebar without waiting
        // for the panel to be closed and reopened.
        if (t === "dm.created" && !dmListRef.current.some((d) => d.conversation_id === conv)) {
          usmApi.get("/messenger/dm/conversations").then((r) => {
            setDmList(r.data || []); setDmListLoaded(true);
          }).catch(() => { /* noop */ });
        }
        const isSelf = msg.author_id === uid;
        const currentlyViewing = openRef.current && activeRef.current === conv;
        if (!isSelf && !currentlyViewing) {
          // Unread bump + ding + toast (only for receivers).
          setConversations((prev) => {
            const c = prev[conv] || { messages: [], loaded: false, unread: 0 };
            return { ...prev, [conv]: { ...c, unread: (c.unread || 0) + 1 } };
          });
          ding();
          const author = msg.message?.author?.name || "Yeni mesaj";
          const preview = (msg.message?.body || "").slice(0, 80) || (msg.message?.attachment ? "📎 Ek dosya" : "");
          try { toast(author, { description: preview }); } catch { /* noop */ }
          const isMention = Array.isArray(msg.mentions) && msg.mentions.includes(uid);
          if (isMention || t === "dm.created") {
            desktopNotify(author + (isMention ? " · @sizden bahsetti" : ""), preview,
              () => { try { setActiveConversation(conv); setTab(conv === "general" ? "general" : "dm"); setOpen(true); } catch { /* noop */ } });
          }
        }
      } else if (t === "chat.updated" || t === "dm.updated") {
        const conv = msg.conversation_id || "general";
        upsertMessage(conv, msg.message);
      } else if (t === "chat.deleted" || t === "dm.deleted") {
        const conv = msg.conversation_id || "general";
        patchMessage(conv, msg.id, { deleted_at: new Date().toISOString(), body: "Bu mesaj silindi.", attachment: null, reactions: {} });
        cascadeReplySnapshotDeleted(msg.id);
      } else if (t === "announcement.created") {
        setConversations((prev) => {
          const c = prev.announcements || { messages: [], loaded: false, unread: 0 };
          return { ...prev, announcements: { ...c, messages: [msg.message, ...c.messages] } };
        });
      } else if (t === "announcement.updated") {
        patchMessage("announcements", msg.message.id, msg.message);
      } else if (t === "announcement.deleted") {
        setConversations((prev) => {
          const c = prev.announcements; if (!c) return prev;
          return { ...prev, announcements: { ...c, messages: c.messages.filter((m) => m.id !== msg.id) } };
        });
        cascadeReplySnapshotDeleted(msg.id);
      } else if (t === "message.reaction") {
        const conv = msg.conversation_id || "general";
        patchMessage(conv, msg.id, { reactions: msg.reactions || {} });
      } else if (t === "typing") {
        if (msg.user_id === uid) return;
        setTypingByConv((prev) => ({
          ...prev,
          [msg.conversation_id]: { ...(prev[msg.conversation_id] || {}), [msg.user_id]: { name: msg.name, ts: Date.now() } },
        }));
      } else if (t === "read") {
        if (msg.user_id === uid) {
          setReadPointers((p) => ({ ...p, mine: { ...p.mine, [msg.conversation_id]: msg.up_to_ts } }));
        } else if (msg.conversation_id === "general") {
          setReadPointers((p) => ({ ...p, othersGeneral: { ...p.othersGeneral, [msg.user_id]: msg.up_to_ts } }));
        }
      } else if (t === "pin.updated") {
        setPins((p) => ({ ...p, [msg.conversation_id]: msg.pin || null }));
      }
    };
    ws.onclose = (ev) => {
      wsRef.current = null;
      stopHeartbeat();
      if (ev.code === 4401) return;   // auth failure — don't spam.
      // 55.2.1 — reconnect indefinitely (no arbitrary retry cap).
      // Exponential backoff up to 30 s.
      const attempt = retryRef.current++;
      const bo = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
      setTimeout(connect, bo);
    };
    ws.onerror = () => {};
  }, [patchMessage, upsertMessage, cascadeReplySnapshotDeleted]);

  useEffect(() => {
    if (!currentUserId) return;
    connect();
    return () => { try { wsRef.current?.close(); } catch { /* noop */ } };
  }, [currentUserId, connect]);

  // 55.2.2 — Tab-visible / online → force resync. Browsers throttle
  // background tabs and can silently pause websocket traffic; on
  // wake we (a) reconnect the socket if it died, and (b) refetch
  // the active conversation so anything missed while the tab was
  // hidden appears instantly. NOT polling: this only fires on
  // real user/OS events (visibilitychange, online).
  useEffect(() => {
    if (!currentUserId) return;
    const resync = () => {
      const ws = wsRef.current;
      const dead = !ws || ws.readyState >= 2; // CLOSING or CLOSED
      if (dead) { try { connect(); } catch { /* noop */ } return; }
      // Alive: just backfill the active conversation + DM list.
      try {
        const active = activeRef.current;
        const ech = ensureConvHistoryRef.current;
        if (ech) {
          ech("general", { force: true });
          if (active && active !== "general" && active !== "announcements") {
            ech(active, { force: true });
          }
        }
        usmApi.get("/messenger/dm/conversations").then((r) => {
          setDmList(r.data || []); setDmListLoaded(true);
        }).catch(() => { /* noop */ });
      } catch { /* noop */ }
    };
    const onVis = () => { if (document.visibilityState === "visible") resync(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", resync);
    window.addEventListener("focus", resync);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", resync);
      window.removeEventListener("focus", resync);
    };
  }, [currentUserId, connect]);

  // Expire stale typing indicators every 2 s.
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setTypingByConv((prev) => {
        let mut = false; const next = {};
        for (const [conv, users] of Object.entries(prev)) {
          const fresh = {};
          for (const [u, v] of Object.entries(users)) {
            if (now - (v.ts || 0) < 5000) fresh[u] = v;
            else mut = true;
          }
          if (Object.keys(fresh).length) next[conv] = fresh;
          else if (users && Object.keys(users).length) mut = true;
        }
        return mut ? next : prev;
      });
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  // ── History / roster / presence loaders ─────────────────────
  const ensureConversationHistory = useCallback(async (convId, { force = false } = {}) => {
    // Cache gate: skip if we've already loaded this conversation and
    // aren't explicitly asking for a fresh sync (e.g., on reconnect).
    if (!force) {
      const c = conversationsRef.current[convId];
      if (c?.loaded) return;
    }
    const endpoint = convId === "announcements" ? "/messenger/announcements" : "/messenger/chat";
    const params = convId === "announcements" ? {} : { limit: 80, conversation_id: convId };
    try {
      const r = await usmApi.get(endpoint, { params });
      const server = r.data || [];
      // 55.2.2 — MERGE (do not REPLACE) so we don't wipe live WS
      // messages that arrived while this fetch was in flight. Server
      // data is authoritative for anything it contains; anything only
      // present in the local cache (real-time msg that beat the
      // fetch or hasn't been persisted yet) is preserved.
      setConversations((prev) => {
        const local = prev[convId]?.messages || [];
        const seen = new Set(server.map((m) => m.id));
        const extras = local.filter((m) => !seen.has(m.id));
        // Announcements come newest-first from the server; regular
        // chat/DM history comes oldest-first. Preserve that ordering.
        const merged = convId === "announcements"
          ? [...server, ...extras]
          : [...server, ...extras].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        return { ...prev, [convId]: { messages: merged, loaded: true, unread: 0 } };
      });
    } catch { /* noop */ }
  }, []);
  useEffect(() => { ensureConvHistoryRef.current = ensureConversationHistory; }, [ensureConversationHistory]);

  const ensureRoster = useCallback(async () => {
    if (roster.length) return roster;
    try {
      const r = await usmApi.get("/messenger/roster");
      setRoster(r.data || []);
      return r.data || [];
    } catch { return []; }
  }, [roster]);

  const ensurePresence = useCallback(async () => {
    try {
      const r = await usmApi.get("/messenger/presence");
      const online = new Set(r.data.online || []);
      // 55.2.1 — belt-and-braces: always mark self as online while
      // the panel is being opened. Prevents an empty flash if the
      // HTTP snapshot beats the WS `hello` frame.
      const uid = userIdRef.current;
      if (uid) online.add(uid);
      setPresence({ online, lastSeen: r.data.last_seen || {} });
    } catch { /* noop */ }
  }, []);

  const ensureReadState = useCallback(async () => {
    try {
      const r = await usmApi.get("/messenger/read");
      setReadPointers({ mine: r.data.mine || {}, othersGeneral: r.data.others_general || {} });
    } catch { /* noop */ }
  }, []);

  const ensureDmList = useCallback(async () => {
    try {
      const r = await usmApi.get("/messenger/dm/conversations");
      setDmList(r.data || []); setDmListLoaded(true);
    } catch { /* noop */ }
  }, []);

  // Phase 55.2 — Pin loader + actions.
  const ensurePins = useCallback(async () => {
    try {
      const r = await usmApi.get("/messenger/pins");
      const map = {};
      for (const p of (r.data || [])) map[p.conversation_id] = p;
      setPins(map);
    } catch { /* noop */ }
  }, []);

  const pinMessage = useCallback(async (conversation_id, message_id) => {
    const r = await usmApi.post(`/messenger/conversations/${encodeURIComponent(conversation_id)}/pin`, { message_id });
    setPins((p) => ({ ...p, [conversation_id]: r.data?.pin || null }));
    return r.data?.pin;
  }, []);

  const unpinConversation = useCallback(async (conversation_id) => {
    await usmApi.delete(`/messenger/conversations/${encodeURIComponent(conversation_id)}/pin`);
    setPins((p) => ({ ...p, [conversation_id]: null }));
  }, []);

  // Phase 55.2 — Draft reply target management (flat, one target per conv).
  const setReplyTarget = useCallback((conversation_id, message) => {
    setReplyTargets((p) => ({ ...p, [conversation_id]: message || null }));
  }, []);
  const clearReplyTarget = useCallback((conversation_id) => {
    setReplyTargets((p) => ({ ...p, [conversation_id]: null }));
  }, []);

  const openPanel = useCallback(async () => {
    setOpen(true);
    // Ask browser notification permission on first open (lazy).
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission().catch(() => { /* noop */ });
      }
    } catch { /* noop */ }
    await Promise.all([
      ensureConversationHistory("general"),
      ensureConversationHistory("announcements"),
      ensureRoster(),
      ensurePresence(),
      ensureReadState(),
      ensureDmList(),
      ensurePins(),
    ]);
  }, [ensureConversationHistory, ensureRoster, ensurePresence, ensureReadState, ensureDmList, ensurePins]);

  const closePanel = useCallback(() => setOpen(false), []);

  const switchConversation = useCallback(async (convId) => {
    setActiveConversation(convId);
    // Reset unread for the conversation we're switching to.
    setConversations((prev) => {
      const c = prev[convId]; if (!c) return prev;
      return { ...prev, [convId]: { ...c, unread: 0 } };
    });
    if (convId.startsWith("dm:") && !conversationsRef.current[convId]?.loaded) {
      await ensureConversationHistory(convId);
    }
  }, [ensureConversationHistory]);

  // ── Actions ─────────────────────────────────────────────────
  const sendChat = useCallback(async ({ body, attachment, conversation_id = "general", reply_to_id = null }) => {
    const r = await usmApi.post(`/messenger/chat?conversation_id=${encodeURIComponent(conversation_id)}`, { body, attachment: attachment || null, reply_to_id: reply_to_id || null });
    upsertMessage(conversation_id, r.data);
    return r.data;
  }, [upsertMessage]);

  const editMessage = useCallback(async (m, body) => {
    const r = await usmApi.patch(`/messenger/chat/${m.id}`, { body });
    upsertMessage(m.conversation_id || "general", r.data);
    return r.data;
  }, [upsertMessage]);

  const deleteMessage = useCallback(async (m) => {
    await usmApi.delete(`/messenger/chat/${m.id}`);
    patchMessage(m.conversation_id || "general", m.id, { deleted_at: new Date().toISOString(), body: "Bu mesaj silindi.", attachment: null, reactions: {} });
  }, [patchMessage]);

  const createAnnouncement = useCallback(async ({ body, attachment, reply_to_id = null }) => {
    const r = await usmApi.post("/messenger/announcements", { body, attachment: attachment || null, reply_to_id: reply_to_id || null });
    setConversations((prev) => {
      const c = prev.announcements || { messages: [], loaded: false, unread: 0 };
      return { ...prev, announcements: { ...c, messages: [r.data, ...c.messages] } };
    });
    return r.data;
  }, []);

  const editAnnouncement = useCallback(async (id, body) => {
    const r = await usmApi.patch(`/messenger/announcements/${id}`, { body });
    patchMessage("announcements", id, r.data);
    return r.data;
  }, [patchMessage]);

  const deleteAnnouncement = useCallback(async (id) => {
    await usmApi.delete(`/messenger/announcements/${id}`);
    setConversations((prev) => {
      const c = prev.announcements; if (!c) return prev;
      return { ...prev, announcements: { ...c, messages: c.messages.filter((m) => m.id !== id) } };
    });
  }, []);

  const toggleReaction = useCallback(async (mid, emoji) => {
    await usmApi.post(`/messenger/messages/${mid}/react`, { emoji });
    // WS broadcast will update the local cache.
  }, []);

  const markConversationRead = useCallback(async (convId, upToTs) => {
    if (!convId || !upToTs) return;
    try { await usmApi.post("/messenger/read", { conversation_id: convId, up_to_ts: upToTs }); }
    catch { /* noop */ }
  }, []);

  const notifyTyping = useCallback(async (convId) => {
    try { await usmApi.post("/messenger/typing", { conversation_id: convId }); }
    catch { /* noop */ }
  }, []);

  const startDm = useCallback(async (userId) => {
    const r = await usmApi.post("/messenger/dm/start", { user_id: userId });
    return r.data;   // {conversation_id, peer}
  }, []);

  const uploadAttachment = useCallback(async (file) => {
    if (!file) return null;
    if (file.size > 20 * 1024 * 1024) throw new Error("Dosya çok büyük — maks 20 MB");
    const fd = new FormData();
    fd.append("file", file, file.name);
    const r = await usmApi.post("/messenger/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  }, []);

  const search = useCallback(async (q, opts = {}) => {
    if (!q) return [];
    const r = await usmApi.get("/messenger/search", { params: { q, ...opts } });
    return r.data || [];
  }, []);

  const fetchPreview = useCallback(async (prefix, code) => {
    const map = { UZG: "inventory", SAT: "sale", CUS: "customer", TRF: "transfer" };
    const kind = map[prefix]; if (!kind) return null;
    const r = await usmApi.get(`/messenger/preview/${kind}/${encodeURIComponent(code)}`);
    return r.data;
  }, []);

  // Derived legacy view (V1 compat)
  const chat = conversations.general?.messages || [];
  const announcements = conversations.announcements?.messages || [];
  const totalUnread = Object.values(conversations).reduce((s, c) => s + (c?.unread || 0), 0)
    + dmList.reduce((s, d) => s + (d.unread || 0), 0);

  const value = useMemo(() => ({
    // V1 shape (kept for backwards compat with the old panel — not used any more, but harmless).
    open, unread: totalUnread, chat, announcements,
    online: presence.online.size,
    // V1.1 additions
    conversations, activeConversation, tab, setTab,
    dmList, dmListLoaded,
    roster, presence, readPointers, typingByConv,
    // V1.2 additions — pins & replies
    pins, replyTargets, setReplyTarget, clearReplyTarget,
    pinMessage, unpinConversation,
    openPanel, closePanel, switchConversation,
    sendChat, editChat: editMessage, deleteChat: deleteMessage,
    createAnnouncement, editAnnouncement, deleteAnnouncement,
    toggleReaction, markConversationRead, notifyTyping,
    startDm, uploadAttachment, search, fetchPreview,
    ensureRoster, ensureConversationHistory, ensureDmList,
  }), [open, totalUnread, chat, announcements, conversations, activeConversation, tab,
       dmList, dmListLoaded, roster, presence, readPointers, typingByConv,
       pins, replyTargets, setReplyTarget, clearReplyTarget, pinMessage, unpinConversation,
       openPanel, closePanel, switchConversation,
       sendChat, editMessage, deleteMessage,
       createAnnouncement, editAnnouncement, deleteAnnouncement,
       toggleReaction, markConversationRead, notifyTyping,
       startDm, uploadAttachment, search, fetchPreview,
       ensureRoster, ensureConversationHistory, ensureDmList]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMessenger() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useMessenger must be inside MessengerProvider");
  return v;
}
